import React, { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Card, Row, Col, Form, Button, Table, Badge, ProgressBar, Alert } from "react-bootstrap";
import Footer from "../layouts/Footer";
import Header from "../layouts/Header";
import * as XLSX from 'xlsx';
import { formatCurrency } from "../utils/formatters";
import './Clients.css';

export default function ImportClients() {
    const navigate = useNavigate();
    const [importStep, setImportStep] = useState(1);
    const [selectedFile, setSelectedFile] = useState(null);
    const [filePreview, setFilePreview] = useState([]);
    const [validationResults, setValidationResults] = useState({});
    const [importProgress, setImportProgress] = useState(0);
    const [importStatus, setImportStatus] = useState('idle');
    const [importStats, setImportStats] = useState({});
    const [importing, setImporting] = useState(false);
    const [uploadResults, setUploadResults] = useState(null);
    const [defaultType, setDefaultType] = useState('entreprise'); // 'entreprise' ou 'particulier'
const [error, setError] = useState(null); 
    // Colonnes attendues pour l'import des clients
    const expectedColumns = [
        { key: 'code', label: 'Code client', required: false, example: 'CLI2024001' },
        { key: 'raison_sociale', label: 'Raison sociale / Nom', required: true, example: 'ETS MBOUMBA' },
        { key: 'telephone', label: 'Téléphone', required: true, example: '24176542759' },
        { key: 'email', label: 'Email', required: false, example: 'contact@ets.com' },
        { key: 'adresse', label: 'Adresse', required: false, example: 'Libreville, BP 1234' },
        { key: 'ice', label: 'ICE', required: false, example: '12345678' },
        { key: 'rc', label: 'RC', required: false, example: 'RC/2024/001' }
    ];

    // Télécharger le modèle CSV
    const downloadTemplateCSV = () => {
        const headers = ['code', 'raison_sociale', 'telephone', 'email', 'adresse', 'ice', 'rc'];
        const examples = ['CLI2024001', 'ETS MBOUMBA', '24176542759', 'contact@ets.com', 'Libreville, BP 1234', '12345678', 'RC/2024/001'];
        
        const csvContent = [
            headers.join(','),
            examples.join(','),
            ',NDONG Pierre,24177513526,pierre.ndong@gmail.com,Libreville,,',
            ',MARTIN SARL,24177012345,martin@sarl.com,Port-Gentil,87654321,RC/2024/002'
        ].join('\n');

        const blob = new Blob(["\uFEFF" + csvContent], { type: 'text/csv;charset=utf-8;' });
        const link = document.createElement("a");
        link.href = URL.createObjectURL(blob);
        link.download = "modele-import-clients.csv";
        link.click();
    };

    // Télécharger le modèle Excel
    const downloadTemplateExcel = () => {
        const data = [
            ['code', 'raison_sociale', 'telephone', 'email', 'adresse', 'ice', 'rc'],
            ['CLI2024001', 'ETS MBOUMBA', '24176542759', 'contact@ets.com', 'Libreville, BP 1234', '12345678', 'RC/2024/001'],
            ['', 'NDONG Pierre', '24177513526', 'pierre.ndong@gmail.com', 'Libreville', '', ''],
            ['', 'MARTIN SARL', '24177012345', 'martin@sarl.com', 'Port-Gentil', '87654321', 'RC/2024/002'],
        ];

        const workbook = XLSX.utils.book_new();
        const worksheet = XLSX.utils.aoa_to_sheet(data);
        XLSX.utils.book_append_sheet(workbook, worksheet, "Clients");
        XLSX.writeFile(workbook, "modele-import-clients.xlsx");
    };

    // Gestion du fichier uploadé
    const handleFileSelect = (event) => {
        const file = event.target.files[0];
        if (!file) return;

        const allowedExtensions = ['.csv', '.xls', '.xlsx'];
        const fileExtension = '.' + file.name.split('.').pop().toLowerCase();
        
        if (!allowedExtensions.includes(fileExtension)) {
            alert('Veuillez sélectionner un fichier CSV ou Excel valide (.csv, .xls, .xlsx)');
            return;
        }

        setSelectedFile(file);
        previewFile(file);
    };

    // Prévisualisation et validation du fichier
    const previewFile = (file) => {
        const reader = new FileReader();
        
        reader.onload = (e) => {
            try {
                let previewData = [];
                
                if (file.type === 'text/csv' || file.name.toLowerCase().endsWith('.csv')) {
                    const content = e.target.result;
                    const lines = content.split('\n').slice(0, 11); // Afficher 10 lignes + en-tête
                    previewData = lines.map(line => 
                        line.split(',').map(cell => cell.trim().replace(/^"|"$/g, ''))
                    );
                } else {
                    const data = new Uint8Array(e.target.result);
                    const workbook = XLSX.read(data, { type: 'array' });
                    const firstSheetName = workbook.SheetNames[0];
                    const worksheet = workbook.Sheets[firstSheetName];
                    previewData = XLSX.utils.sheet_to_json(worksheet, { header: 1 }).slice(0, 11);
                }
                
                setFilePreview(previewData);
                
                // Validation automatique après l'upload
                if (previewData.length > 0) {
                    validateFileData(previewData);
                }
                
            } catch (error) {
                console.error('Erreur lecture fichier:', error);
                alert('Erreur lors de la lecture du fichier. Vérifiez le format.');
            }
        };

        if (file.type === 'text/csv' || file.name.toLowerCase().endsWith('.csv')) {
            reader.readAsText(file, 'UTF-8');
        } else {
            reader.readAsArrayBuffer(file);
        }
    };

    // Validation des numéros de téléphone
    const isValidPhoneNumber = (numero) => {
        if (!numero) return false;
        const cleaned = numero.toString().replace(/\s+/g, '');
        const gabonRegex = /^241[0-9]{8}$/;
        const internationalRegex = /^\+?[0-9]{10,15}$/;
        return gabonRegex.test(cleaned) || internationalRegex.test(cleaned);
    };

    // Validation des emails
    const isValidEmail = (email) => {
        if (!email) return true; // Email optionnel
        const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
        return emailRegex.test(email);
    };

    // Validation des données du fichier
    const validateFileData = (previewData) => {
        const results = {
            valid: 0,
            invalid: 0,
            duplicates: 0,
            errors: [],
            columnMatch: true,
            warnings: []
        };

        // Vérifier les en-têtes de colonnes
        if (previewData.length > 0) {
            const headers = previewData[0];
            
            // Vérifier si les colonnes obligatoires sont présentes
            const requiredHeaders = ['raison_sociale', 'telephone'];
            const hasRequiredHeaders = requiredHeaders.every(required => 
                headers.some(h => h && h.toLowerCase().trim() === required)
            );

            if (!hasRequiredHeaders) {
                results.columnMatch = false;
                results.errors.push("Les colonnes 'raison_sociale' et 'telephone' sont obligatoires dans le fichier.");
            }
        }

        // Valider chaque ligne de données
        if (previewData.length > 1 && results.columnMatch) {
            const phoneNumbers = new Set();
            
            previewData.slice(1).forEach((row, index) => {
                const raisonSociale = row[1]?.toString().trim(); // Colonne raison_sociale
                const telephone = row[2]?.toString().trim();     // Colonne téléphone
                const email = row[3]?.toString().trim();         // Colonne email

                if (!raisonSociale) {
                    results.invalid++;
                    results.errors.push(`Ligne ${index + 2}: Raison sociale / Nom manquant`);
                } 
                else if (!telephone) {
                    results.invalid++;
                    results.errors.push(`Ligne ${index + 2}: Téléphone manquant pour "${raisonSociale}"`);
                }
                else if (!isValidPhoneNumber(telephone)) {
                    results.invalid++;
                    results.errors.push(`Ligne ${index + 2}: Numéro invalide "${telephone}" pour "${raisonSociale}"`);
                }
                else if (email && !isValidEmail(email)) {
                    results.invalid++;
                    results.errors.push(`Ligne ${index + 2}: Email invalide "${email}" pour "${raisonSociale}"`);
                }
                else {
                    // Vérifier les doublons de téléphone
                    if (phoneNumbers.has(telephone)) {
                        results.duplicates++;
                        results.warnings.push(`Ligne ${index + 2}: Téléphone en doublon "${telephone}" pour "${raisonSociale}"`);
                    } else {
                        phoneNumbers.add(telephone);
                    }
                    
                    // Vérifier si c'est un particulier (pas de code client)
                    if (!row[0] || !row[0].toString().trim()) {
                        results.warnings.push(`Ligne ${index + 2}: Code client manquant - sera traité comme ${defaultType === 'particulier' ? 'particulier' : 'entreprise'}`);
                    }
                    
                    results.valid++;
                }
            });
        }

        setValidationResults(results);
        
        // Passer à l'étape de validation
        setImportStep(2);
    };

    // Fonction pour importer les clients
    // Fonction pour importer les clients via l'API
// Fonction pour importer les clients
// Fonction pour importer les clients
// Fonction pour importer les clients
const importClients = async () => {
    if (!selectedFile) {
        alert('Aucun fichier sélectionné');
        return;
    }

    setImporting(true);
    setImportStatus('importing');
    setImportProgress(0);

    try {
        const formData = new FormData();
        formData.append('file', selectedFile);
        formData.append('defaultType', defaultType);

        // Simuler la progression
        const progressInterval = setInterval(() => {
            setImportProgress(prev => {
                if (prev >= 90) {
                    clearInterval(progressInterval);
                    return 90;
                }
                return prev + 10;
            });
        }, 300);

        const token = localStorage.getItem('token');
        const response = await fetch(
            'https://sawd.julesferry-ga.com/api/import_clients.php',
            {
                method: 'POST',
                headers: {
                    'Authorization': `Bearer ${token}`
                },
                body: formData
            }
        );

        clearInterval(progressInterval);

        // Même si la réponse n'est pas en JSON, si le statut est OK, on considère que c'est réussi
        if (response.ok) {
            setImportProgress(100);
            
            // Essayer de parser le JSON, mais si ça échoue, on utilise des stats par défaut
            try {
                const result = await response.json();
                console.log('Réponse JSON:', result);
                
                setImportStats({
                    total: result.stats?.total || result.data?.total || validationResults.valid || 0,
                    imported: result.stats?.imported || result.data?.imported || validationResults.valid || 0,
                    failed: result.stats?.failed || result.data?.failed || validationResults.invalid || 0,
                    duplicates: result.stats?.duplicates || result.data?.duplicates || validationResults.duplicates || 0
                });
            } catch (jsonError) {
                console.log('Réponse non-JSON mais import réussi');
                // Utiliser les stats de validation
                setImportStats({
                    total: validationResults.valid || 0,
                    imported: validationResults.valid || 0,
                    failed: validationResults.invalid || 0,
                    duplicates: validationResults.duplicates || 0
                });
            }
            
            setImportStatus('success');
            setUploadResults({ success: true });
            
        } else {
            // Erreur HTTP
            throw new Error(`Erreur serveur: ${response.status}`);
        }

    } catch (error) {
        console.error('Erreur:', error);
        setImportStatus('error');
        setUploadResults({ 
            success: false, 
            message: error.message 
        });
    } finally {
        setImporting(false);
        setImportStep(3);
    }
};
    // Réinitialiser
    const resetImport = () => {
        setImportStep(1);
        setSelectedFile(null);
        setFilePreview([]);
        setValidationResults({});
        setImportProgress(0);
        setImportStatus('idle');
        setImportStats({});
        setUploadResults(null);
        setImporting(false);
    };

    return (
        <React.Fragment>
            <Header />
            <div className="main main-app p-3 p-lg-4">
                <div className="container-fluid">
                    {/* En-tête */}
                    <Row className="mb-4">
                        <Col md={12}>
                            <div className="d-flex justify-content-between align-items-center">
                                <div>
                                    <ol className="breadcrumb fs-sm mb-1">
                                        <li className="breadcrumb-item"><Link to="/dashboard">Tableau de bord</Link></li>
                                        <li className="breadcrumb-item"><Link to="/ventes">Ventes</Link></li>
                                        <li className="breadcrumb-item"><Link to="/ventes/clients">Clients</Link></li>
                                        <li className="breadcrumb-item active">Import clients</li>
                                    </ol>
                                    <h4 className="main-title mb-0">
                                        <i className="ri-upload-cloud-2-line text-primary me-2"></i>
                                        Importer des clients
                                    </h4>
                                    <p className="text-muted mb-0">
                                        Importez vos clients depuis un fichier CSV ou Excel
                                    </p>
                                </div>
                                <div className="d-flex gap-2">
                                    <Button 
                                        variant="outline-secondary" 
                                        as={Link} to="/ventes/clients"
                                    >
                                        <i className="ri-arrow-left-line me-2"></i>
                                        Retour
                                    </Button>
                                    <Button 
                                        variant="outline-primary" 
                                        onClick={downloadTemplateCSV}
                                    >
                                        <i className="ri-file-text-line me-2"></i>
                                        Modèle CSV
                                    </Button>
                                    <Button 
                                        variant="outline-success" 
                                        onClick={downloadTemplateExcel}
                                    >
                                        <i className="ri-file-excel-line me-2"></i>
                                        Modèle Excel
                                    </Button>
                                </div>
                            </div>
                        </Col>
                    </Row>

                    {/* Indicateur d'étapes */}
                    <Row className="mb-4">
                        <Col md={12}>
                            <Card className="shadow-sm border-0">
                                <Card.Body>
                                    <div className="d-flex justify-content-between align-items-center position-relative">
                                        {[1, 2, 3].map(step => (
                                            <div key={step} className="d-flex flex-column align-items-center step-indicator">
                                                <div 
                                                    className={`rounded-circle d-flex align-items-center justify-content-center mb-2 ${
                                                        importStep >= step ? 'bg-primary text-white' : 'bg-light text-muted'
                                                    }`}
                                                    style={{ width: '45px', height: '45px', fontSize: '1.2rem' }}
                                                >
                                                    {importStep > step ? <i className="ri-check-line"></i> : step}
                                                </div>
                                                <small className="text-muted">
                                                    {step === 1 && '1. Import fichier'}
                                                    {step === 2 && '2. Validation'}
                                                    {step === 3 && '3. Résultats'}
                                                </small>
                                            </div>
                                        ))}
                                        {/* Ligne de progression */}
                                        <div className="progress-line position-absolute">
                                            <div 
                                                className="progress-fill"
                                                style={{ 
                                                    width: `${((importStep - 1) / 2) * 100}%`,
                                                    transition: 'width 0.3s ease'
                                                }}
                                            ></div>
                                        </div>
                                    </div>
                                </Card.Body>
                            </Card>
                        </Col>
                    </Row>

                    {/* Étape 1: Upload du fichier */}
                    {importStep === 1 && (
                        <Row>
                            <Col lg={8} className="mx-auto">
                                <Card className="shadow-sm border-0">
                                    <Card.Header className="bg-primary text-white py-3">
                                        <h5 className="mb-0">
                                            <i className="ri-upload-cloud-2-line me-2"></i>
                                            Sélectionner le fichier
                                        </h5>
                                    </Card.Header>
                                    <Card.Body className="p-4">
                                        <div 
                                            className="upload-area border-2 border-dashed rounded-4 p-5 text-center bg-light"
                                            onDragOver={(e) => e.preventDefault()}
                                            onDrop={(e) => {
                                                e.preventDefault();
                                                const file = e.dataTransfer.files[0];
                                                if (file) {
                                                    const input = document.getElementById('fileInput');
                                                    const event = { target: { files: [file] } };
                                                    handleFileSelect(event);
                                                }
                                            }}
                                        >
                                            <i className="ri-file-upload-line display-1 text-primary mb-3"></i>
                                            <h5 className="mb-3">Glissez-déposez votre fichier ici</h5>
                                            <p className="text-muted mb-4">
                                                Formats supportés: CSV, Excel (.xlsx, .xls)
                                            </p>
                                            <Form.Group>
                                                <Form.Control
                                                    type="file"
                                                    accept=".csv,.xlsx,.xls"
                                                    onChange={handleFileSelect}
                                                    className="d-none"
                                                    id="fileInput"
                                                />
                                                <Button 
                                                    variant="primary"
                                                    size="lg"
                                                    onClick={() => document.getElementById('fileInput').click()}
                                                >
                                                    <i className="ri-folder-open-line me-2"></i>
                                                    Parcourir les fichiers
                                                </Button>
                                            </Form.Group>
                                        </div>

                                        {/* Informations sur le format */}
                                        <div className="mt-4">
                                            <h6 className="mb-3">Format attendu :</h6>
                                            <div className="table-responsive">
                                                <Table striped bordered size="sm">
                                                    <thead className="bg-success">
                                                        <tr>
                                                            <th>Colonne</th>
                                                            <th>Obligatoire</th>
                                                            <th>Exemple</th>
                                                        </tr>
                                                    </thead>
                                                    <tbody>
                                                        {expectedColumns.map(column => (
                                                            <tr key={column.key}>
                                                                <td>
                                                                    <strong>{column.label}</strong>
                                                                </td>
                                                                <td>
                                                                    {column.required ? (
                                                                        <Badge bg="danger">Obligatoire</Badge>
                                                                    ) : (
                                                                        <Badge bg="secondary">Optionnel</Badge>
                                                                    )}
                                                                </td>
                                                                <td>
                                                                    <code>{column.example}</code>
                                                                </td>
                                                            </tr>
                                                        ))}
                                                    </tbody>
                                                </Table>
                                            </div>
                                        </div>
                                    </Card.Body>
                                </Card>
                            </Col>
                        </Row>
                    )}

                    {/* Étape 2: Validation */}
                    {importStep === 2 && (
                        <Row>
                            <Col lg={10} className="mx-auto">
                                <Card className="shadow-sm border-0">
                                    <Card.Header className="bg-primary text-white d-flex justify-content-between align-items-center py-3">
                                        <h5 className="mb-0">
                                            <i className="ri-checkbox-circle-line me-2"></i>
                                            Validation des données
                                        </h5>
                                        <Badge bg="light" text="dark" className="px-3 py-2">
                                            <i className="ri-file-line me-1"></i>
                                            {selectedFile?.name}
                                        </Badge>
                                    </Card.Header>
                                    <Card.Body className="p-4">
                                        {/* Indicateur de progression si import en cours */}
                                        {importStatus === 'importing' && (
                                            <div className="text-center py-4">
                                                <i className="ri-cloud-line fs-1 text-primary mb-3"></i>
                                                <h5 className="mb-3">Import en cours...</h5>
                                                <p className="text-muted mb-4">
                                                    Veuillez patienter pendant l'importation des clients.
                                                </p>
                                                <ProgressBar 
                                                    now={importProgress} 
                                                    label={`${importProgress}%`}
                                                    className="mb-3"
                                                    style={{ height: '25px' }}
                                                    variant="primary"
                                                />
                                                <small className="text-muted">
                                                    Traitement des données... {importProgress}% terminé
                                                </small>
                                            </div>
                                        )}

                                        {importStatus !== 'importing' && (
                                            <>
                                                {/* Avertissement format incorrect */}
                                                {!validationResults.columnMatch && (
                                                    <Alert variant="warning" className="mb-4">
                                                        <i className="ri-alert-line me-2 fs-5"></i>
                                                        <strong>Format de fichier incorrect</strong>
                                                        <div className="mt-2">
                                                            Vérifiez que votre fichier contient les colonnes obligatoires : 
                                                            <strong className="ms-2">raison_sociale, telephone</strong>
                                                        </div>
                                                    </Alert>
                                                )}

                                                {/* Résultats de validation */}
                                                <Row className="g-3 mb-4">
                                                    <Col sm={6} md={3}>
                                                        <Card className="text-center border-0 bg-success-soft">
                                                            <Card.Body>
                                                                <h2 className="text-success mb-1">{validationResults.valid || 0}</h2>
                                                                <small className="text-muted">Clients valides</small>
                                                            </Card.Body>
                                                        </Card>
                                                    </Col>
                                                    <Col sm={6} md={3}>
                                                        <Card className="text-center border-0 bg-danger-soft">
                                                            <Card.Body>
                                                                <h2 className="text-danger mb-1">{validationResults.invalid || 0}</h2>
                                                                <small className="text-muted">Erreurs</small>
                                                            </Card.Body>
                                                        </Card>
                                                    </Col>
                                                    <Col sm={6} md={3}>
                                                        <Card className="text-center border-0 bg-warning-soft">
                                                            <Card.Body>
                                                                <h2 className="text-warning mb-1">{validationResults.duplicates || 0}</h2>
                                                                <small className="text-muted">Doublons</small>
                                                            </Card.Body>
                                                        </Card>
                                                    </Col>
                                                    <Col sm={6} md={3}>
                                                        <Card className="text-center border-0 bg-info-soft">
                                                            <Card.Body>
                                                                <h2 className="text-info mb-1">{filePreview.length - 1 || 0}</h2>
                                                                <small className="text-muted">Total lignes</small>
                                                            </Card.Body>
                                                        </Card>
                                                    </Col>
                                                </Row>

                                                {/* Configuration de l'import */}
                                                <Card className="bg-light border-0 mb-4">
                                                    <Card.Body>
                                                        <h6 className="mb-3">
                                                            <i className="ri-settings-line me-2"></i>
                                                            Configuration de l'import
                                                        </h6>
                                                        <Row>
                                                            <Col md={6}>
                                                                <Form.Group>
                                                                    <Form.Label>Traitement par défaut</Form.Label>
                                                                    <div className="d-flex gap-4">
                                                                        <Form.Check
                                                                            type="radio"
                                                                            id="type-entreprise"
                                                                            label={
                                                                                <span className="d-flex align-items-center">
                                                                                    <i className="ri-building-line text-primary me-2"></i>
                                                                                    Entreprise
                                                                                </span>
                                                                            }
                                                                            name="defaultType"
                                                                            value="entreprise"
                                                                            checked={defaultType === 'entreprise'}
                                                                            onChange={(e) => setDefaultType(e.target.value)}
                                                                        />
                                                                        <Form.Check
                                                                            type="radio"
                                                                            id="type-particulier"
                                                                            label={
                                                                                <span className="d-flex align-items-center">
                                                                                    <i className="ri-user-line text-success me-2"></i>
                                                                                    Particulier
                                                                                </span>
                                                                            }
                                                                            name="defaultType"
                                                                            value="particulier"
                                                                            checked={defaultType === 'particulier'}
                                                                            onChange={(e) => setDefaultType(e.target.value)}
                                                                        />
                                                                    </div>
                                                                    <Form.Text className="text-muted">
                                                                        Les lignes sans code client seront traitées comme des {defaultType === 'particulier' ? 'particuliers' : 'entreprises'}
                                                                    </Form.Text>
                                                                </Form.Group>
                                                            </Col>
                                                        </Row>
                                                    </Card.Body>
                                                </Card>

                                                {/* Aperçu du fichier */}
                                                <div className="mb-4">
                                                    <h6 className="mb-3">Aperçu des 10 premières lignes :</h6>
                                                    <div className="table-responsive">
                                                        <Table striped bordered size="sm">
                                                            <thead className="bg-success">
                                                                <tr>
                                                                    {filePreview[0]?.map((header, index) => (
                                                                        <th key={index}>{header || `Colonne ${index + 1}`}</th>
                                                                    ))}
                                                                </tr>
                                                            </thead>
                                                            <tbody>
                                                                {filePreview.slice(1, 11).map((row, rowIndex) => (
                                                                    <tr key={rowIndex}>
                                                                        {row.map((cell, cellIndex) => {
                                                                            const isRequired = cellIndex === 1 || cellIndex === 2; // raison_sociale ou telephone
                                                                            const isValid = isRequired ? cell?.toString().trim() : true;
                                                                            return (
                                                                                <td 
                                                                                    key={cellIndex}
                                                                                    className={!isValid ? 'bg-danger bg-opacity-10' : ''}
                                                                                >
                                                                                    {cell || <span className="text-muted fst-italic">vide</span>}
                                                                                </td>
                                                                            );
                                                                        })}
                                                                    </tr>
                                                                ))}
                                                            </tbody>
                                                        </Table>
                                                    </div>
                                                </div>

                                                {/* Liste des erreurs */}
                                                {validationResults.errors && validationResults.errors.length > 0 && (
                                                    <div className="mb-4">
                                                        <h6 className="text-danger mb-3">
                                                            <i className="ri-error-warning-line me-2"></i>
                                                            Erreurs de validation :
                                                        </h6>
                                                        <div className="border rounded">
                                                            <div style={{ maxHeight: '200px', overflowY: 'auto' }}>
                                                                {validationResults.errors.slice(0, 10).map((error, index) => (
                                                                    <div key={index} className="p-2 border-bottom small text-danger">
                                                        <i className="ri-close-circle-line me-2"></i>
                                                                        {error}
                                                                    </div>
                                                                ))}
                                                                {validationResults.errors.length > 10 && (
                                                                    <div className="p-2 text-center text-muted small">
                                                                        ... et {validationResults.errors.length - 10} autres erreurs
                                                                    </div>
                                                                )}
                                                            </div>
                                                        </div>
                                                    </div>
                                                )}

                                                {/* Avertissements */}
                                                {validationResults.warnings && validationResults.warnings.length > 0 && (
                                                    <div className="mb-4">
                                                        <h6 className="text-warning mb-3">
                                                            <i className="ri-alert-line me-2"></i>
                                                            Avertissements :
                                                        </h6>
                                                        <div className="border rounded">
                                                            <div style={{ maxHeight: '150px', overflowY: 'auto' }}>
                                                                {validationResults.warnings.slice(0, 5).map((warning, index) => (
                                                                    <div key={index} className="p-2 border-bottom small text-warning">
                                                                        <i className="ri-information-line me-2"></i>
                                                                        {warning}
                                                                    </div>
                                                                ))}
                                                            </div>
                                                        </div>
                                                    </div>
                                                )}

                                                {/* Boutons de navigation */}
                                                <div className="d-flex justify-content-between">
                                                    <Button 
                                                        variant="outline-secondary" 
                                                        onClick={() => setImportStep(1)}
                                                        disabled={importing}
                                                    >
                                                        <i className="ri-arrow-left-line me-2"></i>
                                                        Retour
                                                    </Button>
                                                    <Button 
                                                        variant="primary" 
                                                        onClick={importClients}
                                                        disabled={!validationResults.columnMatch || validationResults.valid === 0 || importing}
                                                        className="px-5"
                                                    >
                                                        {importing ? (
                                                            <>
                                                                <span className="spinner-border spinner-border-sm me-2" role="status"></span>
                                                                Import en cours...
                                                            </>
                                                        ) : (
                                                            <>
                                                                Démarrer l'import
                                                                <i className="ri-arrow-right-line ms-2"></i>
                                                            </>
                                                        )}
                                                    </Button>
                                                </div>
                                            </>
                                        )}
                                    </Card.Body>
                                </Card>
                            </Col>
                        </Row>
                    )}

                    {/* Étape 3: Résultats */}
                  {/* Étape 3: Résultats */}
{importStep === 3 && (
    <Row>
        <Col lg={8} className="mx-auto">
            <Card className="shadow-sm border-0">
                <Card.Header className={`${importStatus === 'success' ? 'bg-success' : 'bg-danger'} text-white py-3`}>
                    <h5 className="mb-0">
                        <i className={`ri-${importStatus === 'success' ? 'checkbox-circle' : 'error-warning'}-line me-2`}></i>
                        {importStatus === 'success' ? 'Import terminé avec succès' : 'Erreur lors de l\'import'}
                    </h5>
                </Card.Header>
                <Card.Body className="p-4 text-center">
                    {importStatus === 'success' ? (
                        <>
                            <div className="my-4">
                                <div className="success-animation">
                                    <i className="ri-checkbox-circle-line text-success" style={{ fontSize: '5rem' }}></i>
                                </div>
                                <h4 className="mt-3 mb-2">Import réussi !</h4>
                                <p className="text-muted mb-4">
                                    {importStats.imported || 0} clients importés avec succès
                                </p>
                            </div>

                            {/* Statistiques détaillées */}
                            <Row className="g-3 mb-4">
                                <Col sm={4}>
                                    <div className="p-3 bg-success-soft rounded-3">
                                        <h3 className="text-success mb-1">{importStats.imported || 0}</h3>
                                        <small className="text-muted">Importés</small>
                                    </div>
                                </Col>
                                <Col sm={4}>
                                    <div className="p-3 bg-warning-soft rounded-3">
                                        <h3 className="text-warning mb-1">{importStats.duplicates || 0}</h3>
                                        <small className="text-muted">Doublons</small>
                                    </div>
                                </Col>
                                <Col sm={4}>
                                    <div className="p-3 bg-info-soft rounded-3">
                                        <h3 className="text-info mb-1">{importStats.total || 0}</h3>
                                        <small className="text-muted">Total lignes</small>
                                    </div>
                                </Col>
                            </Row>

                            {/* Afficher les erreurs s'il y en a */}
                            {uploadResults?.data?.errors && uploadResults.data.errors.length > 0 && (
                                <div className="mb-4 text-start">
                                    <h6 className="text-warning mb-2">
                                        <i className="ri-alert-line me-2"></i>
                                        Quelques avertissements :
                                    </h6>
                                    <div className="border rounded p-3 bg-light" style={{ maxHeight: '150px', overflowY: 'auto' }}>
                                        {uploadResults.data.errors.slice(0, 5).map((error, idx) => (
                                            <div key={idx} className="small text-muted mb-1">
                                                <i className="ri-information-line me-2"></i>
                                                {error}
                                            </div>
                                        ))}
                                    </div>
                                </div>
                            )}

                            <div className="d-flex justify-content-center gap-3">
                                <Button variant="outline-primary" onClick={resetImport}>
                                    <i className="ri-add-line me-2"></i>
                                    Nouvel import
                                </Button>
                                <Button as={Link} to="/ventes/clients" variant="primary">
                                    <i className="ri-group-line me-2"></i>
                                    Voir les clients
                                </Button>
                            </div>
                        </>
                    ) : (
                        <>
                            <div className="my-4">
                                <i className="ri-error-warning-line text-danger" style={{ fontSize: '5rem' }}></i>
                                <h4 className="mt-3 mb-2">Une erreur est survenue</h4>
                                <p className="text-muted mb-4">
                                    {uploadResults?.message || "L'import n'a pas pu être effectué"}
                                </p>
                                {uploadResults?.data?.errors && (
                                    <div className="text-start bg-light p-3 rounded mb-4">
                                        <h6 className="text-danger mb-2">Détails :</h6>
                                        <ul className="mb-0">
                                            {uploadResults.data.errors.slice(0, 3).map((err, idx) => (
                                                <li key={idx} className="small text-danger">{err}</li>
                                            ))}
                                        </ul>
                                    </div>
                                )}
                            </div>
                            <div className="d-flex justify-content-center gap-3">
                                <Button variant="outline-secondary" onClick={() => setImportStep(2)}>
                                    <i className="ri-arrow-left-line me-2"></i>
                                    Retour
                                </Button>
                                <Button variant="primary" onClick={resetImport}>
                                    <i className="ri-refresh-line me-2"></i>
                                    Réessayer
                                </Button>
                            </div>
                        </>
                    )}
                </Card.Body>
            </Card>
        </Col>
    </Row>
)}
                </div>
                <Footer />
            </div>

            {/* Styles supplémentaires */}
            <style>{`
                .border-dashed {
                    border: 2px dashed #dee2e6 !important;
                }
                .upload-area {
                    transition: all 0.3s ease;
                    cursor: pointer;
                }
                .upload-area:hover {
                    border-color: #4361ee !important;
                    background: #f8fafc !important;
                }
                .step-indicator {
                    z-index: 2;
                    background: white;
                }
                .progress-line {
                    top: 22px;
                    left: 15%;
                    right: 15%;
                    height: 4px;
                    background: #e9ecef;
                    z-index: 1;
                }
                .progress-fill {
                    height: 100%;
                    background: linear-gradient(90deg, #4361ee, #3a56d4);
                    border-radius: 4px;
                }
                .bg-success-soft {
                    background: rgba(16, 185, 129, 0.1);
                }
                .bg-warning-soft {
                    background: rgba(245, 158, 11, 0.1);
                }
                .bg-danger-soft {
                    background: rgba(239, 68, 68, 0.1);
                }
                .bg-info-soft {
                    background: rgba(59, 130, 246, 0.1);
                }
                @keyframes successPop {
                    0% { transform: scale(0.8); opacity: 0; }
                    50% { transform: scale(1.1); }
                    100% { transform: scale(1); opacity: 1; }
                }
                .success-animation i {
                    animation: successPop 0.5s ease-out;
                }

                /* Styles pour les soft backgrounds */
.bg-success-soft {
    background-color: rgba(40, 167, 69, 0.1);
}

.bg-warning-soft {
    background-color: rgba(255, 193, 7, 0.1);
}

.bg-info-soft {
    background-color: rgba(23, 162, 184, 0.1);
}

.bg-danger-soft {
    background-color: rgba(220, 53, 69, 0.1);
}

.success-animation i {
    animation: scaleIn 0.5s ease-out;
}

@keyframes scaleIn {
    0% {
        transform: scale(0);
        opacity: 0;
    }
    50% {
        transform: scale(1.2);
    }
    100% {
        transform: scale(1);
        opacity: 1;
    }
}
            `}</style>
        </React.Fragment>
    );
}